import React, { Component } from 'react';
import { get, has, isEqual } from 'lodash';

import toggleInArray from 'component-lib/toggleInArray';

export default function reactForm(options) {
  return function formHelperFn(WrappedComponent) {
    return class FormHelper extends Component {
      constructor(props) {
        super(props);

        const values = options.fields.reduce(
          (all, field) => ({ ...all, [field]: get(options, `defaultValues.${field}`, '') }),
          {},
        );

        this.state = {
          name: options.form,
          values: { ...values },
          originalValues: { ...values },
        };

        this.fieldChangeCallback = () => {};
      }

      componentDidUpdate(prevProps) {
        if (options.onNextProps) {
          options.onNextProps(prevProps, this.props, this.setInitialFields);
        }
      }

      setInitialFields = (values, cb = () => {}) => {
        const updatedValues = Object.keys(values).reduce(
          (all, value) => {
            const modifiedAll = { ...all };

            if (Object.prototype.hasOwnProperty.call(modifiedAll, value)) {
              modifiedAll[value] = values[value];
            }

            return modifiedAll;
          },
          { ...this.state.values },
        );

        this.setState(
          {
            name: options.form,
            values: { ...updatedValues },
            originalValues: { ...updatedValues },
          },
          cb,
        );
      };

      get fields() {
        return Object.keys(this.fieldValues).reduce((all, valueKey) => {
          const modifiedAll = { ...all };

          modifiedAll[valueKey] = {
            value: this.fieldValues[valueKey],
            onChange: ({ target }) => {
              this.setFieldValue(valueKey, target.value);
            },
            change: (value) => {
              this.setFieldValue(valueKey, value);
            },
            toggle: () => {
              const value = this.state.form.values[valueKey];

              this.setFieldValue(valueKey, !value);
            },
            toggleInArray: (value) => {
              this.toggleInFieldArray(valueKey, value);
            },
          };

          return modifiedAll;
        }, {});
      }

      get isDirtyFields() {
        return !isEqual(this.fieldValues, this.state.originalValues);
      }

      get fieldValues() {
        return { ...this.state.values };
      }

      get changedFieldValues() {
        return Object.keys(this.fieldValues).reduce((all, fieldKey) => {
          const modifedChangedValues = { ...all };
          const fieldValue = this.fieldValues[fieldKey];

          const isFieldChanged = !isEqual(fieldValue, this.state.originalValues[fieldKey]);

          if (isFieldChanged) {
            modifedChangedValues[fieldKey] = fieldValue;
          }

          return modifedChangedValues;
        }, {});
      }

      setFields = (newValues) => {
        const modifiedValues = Object.keys(newValues).reduce((all, newValueKey) => {
          const modifiedAll = { ...all };

          if (has(modifiedAll, newValueKey)) {
            modifiedAll[newValueKey] = newValues[newValueKey];
          }

          return modifiedAll;
        }, this.fieldValues);

        this.setState({ values: modifiedValues }, () => this.fieldChangeCallback(newValues));
      };

      setFieldValue = (field, value) => {
        this.setState(
          {
            values: { ...this.fieldValues, [field]: value },
          },
          () => this.fieldChangeCallback({ [field]: value }),
        );
      };

      registerFieldsChangeCallback = (cb) => {
        if (!cb || typeof cb !== 'function') {
          throw new Error('needs to be a function!');
        }

        this.fieldChangeCallback = cb;
      };

      resetFields = (cb) => {
        this.setState(
          {
            values: this.state.originalValues,
          },
          () => {
            if (cb) {
              cb();
            }
          },
        );
      };

      makeFieldsUndirty = () => {
        this.setState({ originalValues: this.fieldValues });
      };

      toggleInFieldArray = (prop, value) => {
        this.setFieldValue(prop, toggleInArray(this.fieldValues[prop], value));
      };

      render() {
        return (
          <WrappedComponent
            {...this.props}
            reactForm={{
              name: this.state.name,
              setInitialFields: this.setInitialFields,
              fields: this.fields,
              setFields: this.setFields,
              isDirtyFields: this.isDirtyFields,
              makeFieldsUndirty: this.makeFieldsUndirty,
              resetFields: this.resetFields,
              registerFieldsChangeCallback: this.registerFieldsChangeCallback,
              values: this.fieldValues,
              originalValues: this.state.originalValues,
              changedFieldValues: this.changedFieldValues || {},
            }}
          />
        );
      }
    };
  };
}
